import { useEffect } from 'react';

interface SEOMetaTagsProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  structuredData?: object;
  language?: 'en' | 'fr' | 'ar' | 'de' | 'es';
}

export const useSEOMetaTags = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  structuredData,
  language = 'en'
}: SEOMetaTagsProps) => {
  useEffect(() => {
    // Update HTML lang attribute dynamically
    const htmlLang = language === 'fr' ? 'fr' : language === 'ar' ? 'ar' : 'en';
    document.documentElement.setAttribute('lang', htmlLang);
    
    // Update title
    document.title = title;
    
    // Update or create meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', description);
    
    // Update or create meta keywords
    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', keywords);
    }
    
    // Update or create canonical URL
    if (canonicalUrl) {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', canonicalUrl);
    }
    
    // Update Open Graph tags
    const updateOGTag = (property: string, content: string) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };
    
    updateOGTag('og:title', title);
    updateOGTag('og:description', description);
    if (ogImage) updateOGTag('og:image', ogImage);
    if (canonicalUrl) updateOGTag('og:url', canonicalUrl);
    
    // Update og:locale based on language
    const ogLocale = language === 'fr' ? 'fr_FR' : 'en_US';
    updateOGTag('og:locale', ogLocale);
    
    // Update or create hreflang tags for multilingual SEO (EN & FR for USA market)
    const baseUrl = canonicalUrl || window.location.origin + window.location.pathname;
    const updateHreflang = (lang: string, href: string) => {
      let hreflang = document.querySelector(`link[hreflang="${lang}"]`);
      if (!hreflang) {
        hreflang = document.createElement('link');
        hreflang.setAttribute('rel', 'alternate');
        hreflang.setAttribute('hreflang', lang);
        document.head.appendChild(hreflang);
      }
      hreflang.setAttribute('href', href);
    };
    
    // Set hreflang tags for all language versions
    updateHreflang('en', baseUrl);
    updateHreflang('en-US', baseUrl);
    updateHreflang('fr', baseUrl + (baseUrl.includes('?') ? '&' : '?') + 'lang=fr');
    updateHreflang('fr-US', baseUrl + (baseUrl.includes('?') ? '&' : '?') + 'lang=fr');
    updateHreflang('x-default', baseUrl);
    
    // Add structured data
    if (structuredData) {
      let script = document.querySelector('script[data-dynamic-seo]');
      if (!script) {
        script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-dynamic-seo', 'true');
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    }
    
    return () => {
      // Cleanup dynamic structured data
      const cleanupScript = document.querySelector('script[data-dynamic-seo]');
      if (cleanupScript) cleanupScript.remove();
    };
  }, [title, description, keywords, canonicalUrl, ogImage, structuredData, language]);
};

